import React from 'react';
import { CheckCircle, XCircle, Loader2, Database, Workflow } from 'lucide-react';

/**
 * Exibe progresso de criação de usuário: schema/tabelas e deploy de workflows
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - result: { provisioning, workflows }
 */
const ProvisioningProgressModal = ({ isOpen, onClose, result }) => {
  if (!isOpen) return null;
  const provisioning = result?.provisioning || { steps: [], errors: [], success: true };
  const workflows = result?.workflows || { success: true, totalDeployed: 0, workflows: [] };

  const renderStepItem = (step, index) => {
    const ok = (() => {
      if (step.type === 'schema') return step.result?.exists && step.result?.permissionOk;
      if (step.type === 'wpp_crm_tables') return step.result?.success;
      if (step.type === 'wpp_check') return step.result?.allExist;
      if (step.type === 'supabase_tables') return step.result?.success;
      if (step.type === 'supabase_check') return Object.values(step.result || {}).every(Boolean);
      return true;
    })();

    const icon = ok ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />;
    const labelMap = {
      schema: `Schema: ${step.target || '-'}`,
      wpp_crm_tables: `Tabelas WhatsApp (CRM): ${step.target || '-'}`,
      wpp_check: `Verificação WhatsApp: ${step.target || '-'}`,
      supabase_tables: `Tabelas Supabase: ${step.target || '-'}`,
      supabase_check: `Verificação Supabase: ${step.target || '-'}`,
    };

    return (
      <div key={index} className="flex items-start gap-3 py-2">
        {icon}
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-white">{labelMap[step.type] || step.type}</div>
          <pre className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words mt-1">
            {JSON.stringify(step.result, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full shadow-2xl border border-gray-200/50 dark:border-gray-700/50 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Provisionamento de Usuário</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white">Fechar</button>
        </div>

        {/* Provisioning Steps */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Schema & Tabelas</span>
          </div>
          {Array.isArray(provisioning.steps) && provisioning.steps.length > 0 ? (
            provisioning.steps.map(renderStepItem)
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">Sem etapas de provisionamento registradas.</div>
          )}
          {provisioning.errors && provisioning.errors.length > 0 && (
            <div className="mt-2 text-sm text-red-600">Erros: {provisioning.errors.join('; ')}</div>
          )}
        </div>

        {/* Workflows */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Workflow className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Deploy de Workflows</span>
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300">Total deployado: {workflows.totalDeployed || 0}</div>
          {Array.isArray(workflows.workflows) && workflows.workflows.length > 0 && (
            <pre className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words mt-1 max-h-48 overflow-auto">
              {JSON.stringify(workflows.workflows, null, 2)}
            </pre>
          )}
          {!workflows.success && workflows.error && (
            <div className="mt-2 text-sm text-red-600">Erro no deploy: {workflows.error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProvisioningProgressModal;